import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Plus, ListTodo, AlertTriangle, Clock, Calendar, User as UserIcon, X, ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { tasks, matters as mattersApi, questions as questionsApi } from '@/lib/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

// Priority + status tokens
const PRIORITY_TONE = {
  low:    'border-slate-200 bg-slate-50 text-slate-600',
  medium: 'border-blue-200 bg-blue-50 text-blue-700',
  high:   'border-orange-200 bg-orange-50 text-orange-700',
  urgent: 'border-red-200 bg-red-50 text-red-700',
};
const STATUS_TONE = {
  open:         'border-slate-200 bg-white text-slate-600',
  in_progress:  'border-amber-200 bg-amber-50 text-amber-700',
  done:         'border-emerald-200 bg-emerald-50 text-emerald-700',
  cancelled:    'border-slate-200 bg-slate-50 text-slate-400',
};

const BUCKET_META = {
  overdue: { label: 'Overdue', tone: 'text-red-700', icon: AlertTriangle },
  today:   { label: 'Due today', tone: 'text-[#2ECC9A]', icon: Zap },
  week:    { label: 'This week', tone: 'text-[#1A3A5C]', icon: Calendar },
  later:   { label: 'Later', tone: 'text-slate-500', icon: Clock },
  undated: { label: 'Undated', tone: 'text-slate-400', icon: ListTodo },
};

export default function TasksPage({ matterId = null, matterRef = null, embedded = false }) {
  const [me, setMe] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [scope, setScope] = React.useState(embedded ? 'matter' : 'mine'); // mine|all|matter
  const [statusFilter, setStatusFilter] = React.useState('active'); // active|all|open|in_progress|done
  const [rmas, setRmas] = React.useState([]);
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  const currentUserId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('immipro_user') || '{}').id; }
    catch { return null; }
  }, []);

  const load = React.useCallback(() => {
    setLoading(true);
    const opts = { include_done: true };
    if (matterId) opts.matter_id = matterId;
    else if (scope === 'mine' && currentUserId) opts.assignee_id = currentUserId;
    if (statusFilter === 'active') opts.include_done = false;
    else if (statusFilter !== 'all') opts.status_filter = statusFilter;
    Promise.all([
      tasks.list(opts).then((r) => { setItems(r.items || []); setCounts(r.counts || {}); }),
      matterId ? Promise.resolve() : tasks.me().then(setMe).catch(() => setMe(null)),
    ]).finally(() => setLoading(false));
  }, [matterId, scope, statusFilter, currentUserId]);

  React.useEffect(load, [load]);
  React.useEffect(() => {
    // RMA roster for assignee picker; questions.rmas returns {items:[…]}
    questionsApi.rmas().then((r) => setRmas(r.items || r || [])).catch(() => setRmas([]));
  }, []);

  const toggleDone = async (t) => {
    const nextStatus = t.status === 'done' ? 'open' : 'done';
    // Optimistic UI: update the row inline so the checkbox feels instant.
    setItems((cur) => cur.map((x) => (x.id === t.id ? { ...x, status: nextStatus } : x)));
    try { await tasks.update(t.id, { status: nextStatus }); load(); }
    catch { toast.error('Update failed'); load(); }
  };

  return (
    <div className={embedded ? '' : 'p-6'} data-testid="tasks-page">
      {!embedded && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
              <ListTodo className="h-4 w-4 text-[#2ECC9A]" />
            </div>
            <div className="flex-1">
              <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Tasks</h1>
              <div className="text-sm text-slate-500">
                Everything on your plate today — plus per-matter to-dos across the firm.
              </div>
            </div>
            <Button data-testid="tasks-new-btn" onClick={() => setComposerOpen(true)}
                    className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
              <Plus className="h-3 w-3" /> New task
            </Button>
          </div>

          {me && me.total > 0 && (
            <div className="mt-4 grid gap-2 md:grid-cols-5" data-testid="tasks-my-buckets">
              {['overdue', 'today', 'week', 'later', 'undated'].map((k) => {
                const meta = BUCKET_META[k];
                const Icon = meta.icon;
                return (
                  <div key={k} className="rounded-md border border-slate-200 bg-white p-3"
                       data-testid={`tasks-bucket-${k}`}>
                    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${meta.tone}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </div>
                    <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">{me.counts[k] || 0}</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {embedded && (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
            <ListTodo className="h-4 w-4 text-[#2ECC9A]" /> Tasks · {counts.total || 0} on this matter
          </div>
          <Button data-testid="tasks-new-btn-inline" onClick={() => setComposerOpen(true)} size="sm"
                  className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <Plus className="h-3 w-3" /> New task
          </Button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        {!embedded && (
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="tasks-scope" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mine">My tasks</SelectItem>
              <SelectItem value="all">All firm tasks</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="tasks-status-filter" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-[10px] text-slate-500">
          {counts.total || 0} total · {counts.open || 0} open · {counts.in_progress || 0} in progress · {counts.done || 0} done
          {counts.overdue > 0 && <span className="ml-2 font-semibold text-red-600">· {counts.overdue} overdue</span>}
        </div>
      </div>

      {loading ? (
        <div className="mt-8 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card className="mt-4 border-dashed border-slate-200">
          <CardContent className="p-8 text-center text-xs text-slate-500">
            {statusFilter !== 'all' && statusFilter !== 'active'
              ? `No ${statusFilter.replace('_', ' ')} tasks.`
              : 'No tasks yet. Click New task to add your first.'}
          </CardContent>
        </Card>
      ) : (
        <div className="mt-4 overflow-hidden rounded-md border border-slate-200" data-testid="tasks-list">
          {items.map((t) => (
            <TaskRow key={t.id} task={t} embedded={embedded}
                     onToggle={() => toggleDone(t)} onEdit={() => setEditing(t)} />
          ))}
        </div>
      )}

      <TaskComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={() => { setComposerOpen(false); load(); }}
        defaultMatterId={matterId}
        defaultMatterRef={matterRef}
        rmas={rmas}
      />

      <TaskEditor
        task={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        rmas={rmas}
      />
    </div>
  );
}


function TaskRow({ task, embedded, onToggle, onEdit }) {
  const isDone = task.status === 'done' || task.status === 'cancelled';
  const overdue = !isDone && task.due_at && new Date(task.due_at) < new Date();
  const due = task.due_at ? new Date(task.due_at) : null;
  return (
    <div
      data-testid={`task-row-${task.id}`}
      className="group flex items-start gap-3 border-t border-slate-100 bg-white px-4 py-3 first:border-t-0 hover:bg-slate-50/40"
    >
      <Checkbox
        data-testid={`task-toggle-${task.id}`}
        checked={isDone}
        onCheckedChange={onToggle}
        className="mt-0.5 data-[state=checked]:bg-[#2ECC9A] data-[state=checked]:border-[#2ECC9A]"
      />
      <button
        onClick={onEdit}
        data-testid={`task-open-${task.id}`}
        className="min-w-0 flex-1 text-left"
      >
        <div className={`flex flex-wrap items-center gap-1.5 text-sm ${isDone ? 'text-slate-400 line-through' : 'text-[#1A3A5C]'}`}>
          <span className="font-medium">{task.title}</span>
          <Badge variant="outline" className={`text-[9px] uppercase ${PRIORITY_TONE[task.priority] || ''}`}>
            {task.priority}
          </Badge>
          {task.status === 'in_progress' && (
            <Badge variant="outline" className={`text-[9px] uppercase ${STATUS_TONE.in_progress}`}>in progress</Badge>
          )}
          {overdue && (
            <Badge variant="outline" className="gap-0.5 text-[9px] uppercase border-red-200 bg-red-50 text-red-700">
              <AlertTriangle className="h-2.5 w-2.5" /> overdue
            </Badge>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          {task.assignee_name ? (
            <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {task.assignee_name}</span>
          ) : (
            <span className="text-slate-400">unassigned</span>
          )}
          {due && (
            <span className={`inline-flex items-center gap-1 ${overdue ? 'font-semibold text-red-600' : ''}`}>
              <Calendar className="h-3 w-3" /> {due.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: due.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })}
            </span>
          )}
          {!embedded && task.matter_ref && (
            <Link to={`/app/matters/${task.matter_id}?tab=tasks`}
                  className="font-mono text-[10px] text-slate-500 hover:text-[#2ECC9A]"
                  onClick={(e) => e.stopPropagation()}>
              {task.matter_ref}
            </Link>
          )}
        </div>
      </button>
    </div>
  );
}


function TaskComposer({ open, onClose, onSaved, defaultMatterId, defaultMatterRef, rmas }) {
  const [title, setTitle] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [matters, setMatters] = React.useState([]);
  const [matterId, setMatterId] = React.useState(defaultMatterId || '');
  const [assigneeId, setAssigneeId] = React.useState('unassigned');
  const [priority, setPriority] = React.useState('medium');
  const [dueAt, setDueAt] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setTitle(''); setDesc('');
      setMatterId(defaultMatterId || '');
      setAssigneeId('unassigned'); setPriority('medium'); setDueAt('');
      if (!defaultMatterId) mattersApi.list().then(setMatters).catch(() => setMatters([]));
    }
  }, [open, defaultMatterId]);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await tasks.create({
        title: title.trim(),
        description: desc.trim() || null,
        matter_id: matterId || null,
        assignee_id: assigneeId === 'unassigned' ? null : assigneeId,
        priority,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      });
      toast.success('Task created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create task'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="task-composer" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
            <ListTodo className="h-4 w-4 text-[#2ECC9A]" /> New task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Title</label>
            <Input data-testid="task-composer-title" value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="e.g. Draft engagement letter for Sharma family"
                   className="mt-1 h-9 text-sm" autoFocus />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Description (optional)</label>
            <Textarea data-testid="task-composer-desc" value={desc} onChange={(e) => setDesc(e.target.value)}
                      rows={3} className="mt-1 text-xs" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {!defaultMatterId && (
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Matter (optional)</label>
                <Select value={matterId || 'none'} onValueChange={(v) => setMatterId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="task-composer-matter" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Firm-wide (no matter)</SelectItem>
                    {matters.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.ref} · {m.applicant}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {defaultMatterRef && (
              <div className="md:col-span-2 rounded bg-slate-50 p-2 text-[11px] text-slate-500">
                On matter <span className="font-mono font-semibold text-[#1A3A5C]">{defaultMatterRef}</span>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Assignee</label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger data-testid="task-composer-assignee" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {rmas.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="task-composer-priority" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Due (optional)</label>
              <Input data-testid="task-composer-due" type="datetime-local"
                     value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                     className="mt-1 h-9 text-xs" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} className="h-8 text-xs">Cancel</Button>
          <Button data-testid="task-composer-save" onClick={save} disabled={saving || !title.trim()}
                  size="sm" className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3" /> Create</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TaskEditor({ task, onClose, onSaved, rmas }) {
  const [title, setTitle] = React.useState('');
  const [desc, setDesc] = React.useState('');
  const [assigneeId, setAssigneeId] = React.useState('unassigned');
  const [priority, setPriority] = React.useState('medium');
  const [status, setStatus] = React.useState('open');
  const [dueAt, setDueAt] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDesc(task.description || '');
      setAssigneeId(task.assignee_id || 'unassigned');
      setPriority(task.priority || 'medium');
      setStatus(task.status || 'open');
      // datetime-local wants YYYY-MM-DDThh:mm (no seconds/tz)
      setDueAt(task.due_at ? task.due_at.slice(0, 16) : '');
    }
  }, [task]);

  if (!task) return null;

  const save = async () => {
    setSaving(true);
    try {
      await tasks.update(task.id, {
        title: title.trim() || undefined,
        description: desc,
        assignee_id: assigneeId === 'unassigned' ? null : assigneeId,
        priority, status,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      });
      toast.success('Task updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this task? It will be removed from the audit trail metadata too.')) return;
    setDeleting(true);
    try { await tasks.remove(task.id); toast.success('Task deleted'); onSaved(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="task-editor" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
            <ListTodo className="h-4 w-4 text-[#2ECC9A]" /> Edit task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <Input data-testid="task-editor-title" value={title} onChange={(e) => setTitle(e.target.value)} className="h-9 text-sm font-medium" />
          <Textarea data-testid="task-editor-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="text-xs" />
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Assignee</label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger data-testid="task-editor-assignee" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {rmas.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="task-editor-priority" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'urgent'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="task-editor-status" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Due</label>
              <Input data-testid="task-editor-due" type="datetime-local"
                     value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                     className="mt-1 h-9 text-xs" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={del} disabled={deleting || saving}
                  className="mr-auto h-8 gap-1 border-red-200 bg-red-50 text-red-700 text-xs">
            <X className="h-3 w-3" /> Delete
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} className="h-8 text-xs">Close</Button>
          <Button data-testid="task-editor-save" onClick={save} disabled={saving || !title.trim()}
                  size="sm" className="h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
